// Datos del cotizador: los planes de la tienda y una simulación por plazo del plan elegido.
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import {
    listStoreQuoterPlans,
    simulateStoreQuoterPlan,
} from '@/api/services/loan-offerings/loan-offerings.client';

export const STORE_QUOTER_KEYS = {
    plans: (storeId: string) => ['storeQuoter', 'plans', storeId] as const,
    simulation: (storeId: string, planId: string, input: SimulationInput, termWeeks: number) =>
        ['storeQuoter', 'simulation', storeId, planId, input, termWeeks] as const,
};

export interface SimulationInput {
    priceCents: number;
    downPaymentPct: number;
    productSku?: string;
}

export function useStoreQuoterPlans(storeId: string) {
    return useQuery({
        queryKey: STORE_QUOTER_KEYS.plans(storeId),
        queryFn: () => listStoreQuoterPlans(storeId),
        enabled: storeId !== '',
        staleTime: 5 * 60_000,
    });
}

/** La consulta de una simulación: llave, lectura y cuándo se pide. */
export function simulationQueryOptions(
    storeId: string,
    planId: string | null,
    input: SimulationInput | null,
    termWeeks: number,
) {
    return {
        queryKey: STORE_QUOTER_KEYS.simulation(
            storeId,
            planId ?? '',
            input ?? { priceCents: 0, downPaymentPct: 0 },
            termWeeks,
        ),
        queryFn: () =>
            simulateStoreQuoterPlan(storeId, planId ?? '', {
                productPriceCents: input?.priceCents ?? 0,
                downPaymentPct: input?.downPaymentPct ?? 0,
                termWeeks,
                productSku: input?.productSku,
            }),
        enabled: storeId !== '' && planId !== null && input !== null && input.priceCents > 0,
        staleTime: 60_000,
    };
}

/** Una consulta por plazo (no hay simulación en lote); la anterior se queda mientras llega la nueva. */
export function useStoreQuoterSimulations(
    storeId: string,
    planId: string | null,
    termWeeksOptions: number[],
    input: SimulationInput | null,
) {
    return useQueries({
        queries: termWeeksOptions.map((termWeeks) => ({
            ...simulationQueryOptions(storeId, planId, input, termWeeks),
            placeholderData: keepPreviousData,
        })),
    });
}
